import { Injectable, computed, signal } from '@angular/core';
import { ModuleType } from '@app/core/enums/module-type.enum';
import { PaymentFlow } from '@app/core/enums/payment.enum';
import { PlanType } from '@app/core/enums/plan-type.enum';
import { GetSmsJourneyPlan } from '@app/core/models';
import { RpLinkData, UnsubmittedPlan } from '@app/core/models/common/sms-journey.interface';
import { Progress } from '@app/core/models/payment/progress.interface';
import { ProposalTreatmentQuoteApiModel } from '@app/core/models/proposal/treatment-quote-proposal.interface';
import { ProviderLatestConfigService } from '@app/rp/services/provider/provider-latest-config.service';
import { RpJourneyProviderService } from '@app/rp/services/provider/provider.service';
import { TreatmentOptionResponse } from '@app/rp/treatment-proposal/models/get-unsubmitted-proposal.response';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PendingPlanProgressService } from './progress/pending-plan-progress.service';
import { RpProposalTemplateService } from './proposal-template/rp-proposal-template.service';

/**
 * this service is used in sms journey
 * TODO: move to smsjourney/rpjourney module?
 */

@Injectable({
  providedIn: 'root'
})
export class PendingPlanService {
  private pendingPlanBehaviorSubject = new BehaviorSubject<UnsubmittedPlan | null>(null);
  private rpLinkDataSubject = new BehaviorSubject<RpLinkData | null>(null);

  private _pendingPlan = signal<UnsubmittedPlan | null>(null);
  readonly pendingPlan = computed(() => this._pendingPlan());

  // ui state, to share across multiple components
  private pendingPlanUiChangesBehaviorSubject = new BehaviorSubject<GetSmsJourneyPlan | null>(null);

  constructor(
    private proposalTemplateService: RpProposalTemplateService,
    private planProgressService: PendingPlanProgressService,
    private rpJourneyProviderService: RpJourneyProviderService,
    private providerLatestConfigService: ProviderLatestConfigService
  ) {}

  initPendingPlan(unsubmittedPlan: UnsubmittedPlan): void {
    this.proposalTemplateService.initProposalTemplate(
      unsubmittedPlan.payment_plan.customrecord_template_proposal
    );
    this.planProgressService.setProgress(unsubmittedPlan.payment_plan.plan_progress as Progress[]);
    this.rpJourneyProviderService.setProviderData(unsubmittedPlan.provider_details);
    this.providerLatestConfigService.setLatestConfig(unsubmittedPlan.provider_details.settings_latest);
    this.setPendingPlan(unsubmittedPlan);
    this.setPlanChanges(unsubmittedPlan.payment_plan);
  }

  setPendingPlan(unsubmittedPlan: UnsubmittedPlan): void {
    const transformed = this.transformUnsubmittedPlan(unsubmittedPlan);
    this.pendingPlanBehaviorSubject.next(transformed);
    this._pendingPlan.set(transformed);
  }

  getPendingPlan(): UnsubmittedPlan | null {
    return this.pendingPlanBehaviorSubject.getValue();
  }

  getPendingPlan$(): Observable<UnsubmittedPlan | null> {
    return this.pendingPlanBehaviorSubject.asObservable();
  }

  setPlanChanges(changes: Partial<GetSmsJourneyPlan>): void {
    const snapshot = this.pendingPlanUiChangesBehaviorSubject.getValue();

    if (!snapshot) {
      this.pendingPlanUiChangesBehaviorSubject.next(this.getPendingPlan()?.payment_plan || null);
    } else {
      this.pendingPlanUiChangesBehaviorSubject.next({
        ...snapshot,
        ...changes
      });
    }
  }

  getPlanChangesSnapshot(): GetSmsJourneyPlan | null {
    return this.pendingPlanUiChangesBehaviorSubject.getValue();
  }

  getPlanChanges$(): Observable<GetSmsJourneyPlan | null> {
    return this.pendingPlanUiChangesBehaviorSubject.asObservable();
  }

  // to align backend and frontend data. only update this if backend has the same data
  updatePaymentPlanState(newValue: Partial<GetSmsJourneyPlan>): void {
    const snapshot = this.getPendingPlan();
    if (snapshot) {
      const newPlan = {
        ...snapshot.payment_plan,
        ...newValue
      };

      this.setPendingPlan({
        ...snapshot,
        payment_plan: newPlan
      });

      // reset the ui changes too
      this.setPlanChanges(newPlan);
    }
  }

  setRpLinkData(rpLinkData: RpLinkData): void {
    this.rpLinkDataSubject.next(rpLinkData);

    const pendingPlan = this.getPendingPlan();
    if (pendingPlan) {
      sessionStorage.setItem(`pv.${pendingPlan.payment_plan.prev_ddr}`, JSON.stringify(rpLinkData));
    }
  }

  getRpLinkData(): RpLinkData | null {
    return this.rpLinkDataSubject.getValue();
  }

  hasPayInFullDetails(): boolean {
    const pif = this.getPifDetails();
    const pifTotalAmount = pif.pifTotalTreatmentCost - pif.pifDiscount - pif.pifOtherDiscount;

    return !!pifTotalAmount;
  }

  isPifPaymentFlow(): boolean {
    const pendingPlan = this.getPendingPlan()?.payment_plan;
    return pendingPlan?.payment_flow === PaymentFlow.PayInFull || false;
  }

  getPifTotalTreatmentCost(): number {
    const pifTotalTreatmentCost = this.getSelectedTreatmentPaymentData()?.pif_total_plan_value;
    if (pifTotalTreatmentCost) {
      return +pifTotalTreatmentCost;
    }
    return 0;
  }

  getPifDetails(): {
    pifTotalTreatmentCost: number;
    pifDiscount: number;
    pifOtherDiscount: number;
    pifOtherDiscountReason: string;
  } {
    const selectedTreatmentOption = this.getSelectedTreatmentPaymentData();
    return {
      pifTotalTreatmentCost: selectedTreatmentOption?.pif_total_plan_value || 0,
      pifDiscount: selectedTreatmentOption?.pif_discount_amount || 0,
      pifOtherDiscount: selectedTreatmentOption?.pif_other_discount_amount || 0,
      pifOtherDiscountReason: selectedTreatmentOption?.pif_other_discount_reason || ''
    };
  }

  isProposalModule(): boolean {
    const pendingPlan = this.getPendingPlan()?.payment_plan;
    return pendingPlan?.custrecord_module_type === ModuleType.Proposal;
  }

  hasPaymentSecured$(): Observable<boolean> {
    return this.getPendingPlan$().pipe(
      map(data => {
        const plan = data?.payment_plan;

        return !!plan?.cc_payment_details;
      })
    );
  }

  getPayDepositTo(): string {
    return this.getSelectedTreatmentOption()?.pay_deposit_to || '';
  }

  getPendingPlanPlanType(): PlanType {
    const pendingPlan = this.getPendingPlan()?.payment_plan;
    return pendingPlan?.plan_type as PlanType;
  }

  getSelectedTreatmentOption(): TreatmentOptionResponse | undefined {
    const pendingPlan = this.getPendingPlan()?.payment_plan;
    return pendingPlan?.treatment_options?.find(option => option.selected);
  }

  getSelectedTreatmentPaymentData():
    | TreatmentOptionResponse
    | ProposalTreatmentQuoteApiModel
    | undefined {
    return this.getSelectedTreatmentOption() || this.getPendingPlan()?.payment_plan.treatment_quote;
  }

  private transformUnsubmittedPlan(data: UnsubmittedPlan): UnsubmittedPlan {
    return {
      ...data,
      payment_plan: this.transformPaymentPlan(data.payment_plan)
    };
  }

  private transformPaymentPlan(plan: GetSmsJourneyPlan): GetSmsJourneyPlan {
    return {
      ...plan,
      treatment_options: plan.treatment_options
        ? this.transformTreatmentOptions(plan.treatment_options)
        : [],
      treatment_quote: this.transformTreatmentQuote(plan)
    };
  }

  private transformTreatmentQuote(plan: GetSmsJourneyPlan): ProposalTreatmentQuoteApiModel {
    if (!plan.treatment_quote) {
      return plan.treatment_quote;
    }

    return {
      ...plan.treatment_quote,
      $$paymentFlowOptions: this.getSelectedPaymentOptions(plan.treatment_quote)
    };
  }

  private transformTreatmentOptions(options: TreatmentOptionResponse[]): TreatmentOptionResponse[] {
    return options.map(option => ({
      ...option,
      $$paymentFlowOptions: this.getSelectedPaymentOptions(option)
    }));
  }

  private getSelectedPaymentOptions(
    treatmentOption: TreatmentOptionResponse | ProposalTreatmentQuoteApiModel
  ): PaymentFlow[] {
    const options = [];

    if (treatmentOption.is_payment_plan_selected) {
      options.push(PaymentFlow.Normal);
    }

    const pifTotalAmount =
      (treatmentOption.pif_total_plan_value || 0) -
      (treatmentOption.pif_discount_amount || 0) -
      (treatmentOption.pif_other_discount_amount || 0);
    if (treatmentOption.is_pif_selected && pifTotalAmount >= 0) {
      options.push(PaymentFlow.PayInFull);
    }

    return options;
  }
}
